import React from 'react';
import { FAQ } from './';
import { useSession } from '../../context/SessionContext';

function ConfirmationScreen({ 
  title, 
  description, 
  timeRequired, 
  onStart, 
  onBack, 
  startButtonText = "Start",
  backButtonText = "Go Back",
  icon = "question",
  faqItems = [],
  faqType = null
}) {
  // Get countdown_enabled from session context
  const { sessionData } = useSession();
  const countdown_enabled = sessionData?.countdown_enabled ?? true; // Default to true if not available
  
  // Render the appropriate icon based on the icon prop
  const renderIcon = () => {
    switch (icon) {
      case 'info':
        return (
          <svg className="mx-auto h-16 w-16" style={{ color: '#33C6B6' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'question':
        return (
          <svg className="mx-auto h-16 w-16" style={{ color: '#33C6B6' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="mx-auto h-16 w-16 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'success':
        return (
          <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="mx-auto h-16 w-16" style={{ color: '#33C6B6' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <div className="w-full py-8">
      <div className="text-center mb-8">
        <div className="mb-6">
          {renderIcon()}
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">{title}</h3>
        <p className="text-xl text-gray-600 mb-6 leading-relaxed pl-8 pr-8">
          {description}
        </p>
        
        {timeRequired && countdown_enabled && (
          <div className="w-full max-w-2xl mx-auto mb-8 bg-blue-50 rounded-lg p-6 border border-blue-100">
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span className="text-xl font-medium text-blue-600">{timeRequired}</span>
              </div>
              <p className="text-sm italic text-blue-500 mt-2">On average, others complete this step in {Math.round(parseInt(timeRequired) * 0.8)} minutes.</p>
            </div>
          </div>
        )}
        <div className="flex justify-center space-x-6">
          <button
            onClick={onBack}
            className="bg-white text-gray-700 border-2 border-gray-600 rounded-lg px-6 py-4 text-xl font-medium hover:bg-gray-50 transition-colors duration-200 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {backButtonText}
          </button>
          <button
            onClick={onStart}
            className="text-white rounded-lg px-8 py-4 text-xl font-medium transition-colors duration-200"
            style={{ backgroundColor: '#33C6B6' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2BA89F'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#33C6B6'}
          >
            {startButtonText}
          </button>
        </div>
      </div>
      
      {/* FAQ Section - shown if faqType is provided */}
      {faqType && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <FAQ faqType={faqType} />
        </div>
      )}
    </div>
  );
}

export default ConfirmationScreen;
